// External
import React, { useState } from "react";
import _ from "lodash";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";

// Design system
import { Button, SelectDropdown, Tabs, EmptyState } from "@toddle-edu/ds-web";
import {
  NoDataIllustration,
  AllCompletedIllustration,
} from "@toddle-edu/ds-theme";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading/Loading";
import FeedListv2 from "AppComponents/FeedListv2";

// Relative
import {
  getAssociatedDataOfAcademicTermQuery,
  getMissingCategoriesForMigrationQuery,
} from "./modules/ViewAndMigrateTermAssociatedDataQueries";
import {
  getMissingCategoriesColumnList,
  getMissingCategoriesFeedData,
  getAssociatedDataColumnList,
  getAssociatedDataFeedData,
  getCustomComponent,
} from "./modules/ViewAndMigrateTermAssociatedDataUtils";
import { migrateTermData } from "./modules/ViewAndMigrateTermAssociatedDataThunks";
import DownloadTableData from "../DownloadTableData/DownloadTableData";

// Styles
import classes from "./ViewAndMigrateTermAssociatedData.scss";

const TAB_STATE = {
  VIEW_SOURCE_TERM_ASSOCIATED_DATA: "VIEW_SOURCE_TERM_ASSOCIATED_DATA",
  CATEGORIES_NEEDED_IN_TARGET_TERM: "CATEGORIES_NEEDED_IN_TARGET_TERM",
};

const TABS = [
  {
    value: TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA,
    label: "View source term associated data",
  },
  {
    value: TAB_STATE.CATEGORIES_NEEDED_IN_TARGET_TERM,
    label: "Categories needed in target term",
  },
];

const styles = {
  tabStyle: {
    justifyContent: "flex-start",
    paddingInline: "12px",
  },
  emptyStateSubtitleStyle: {
    maxWidth: "448px",
  },
};

const ViewAndMigrateTermAssociatedData = ({
  data,
  destinationTerm,
  areNoMissingCategoriesInTargetTerm,
  missingCategoriesForAcademicTermMigration,
  associatedDataOfAcademicTerm,
  handleDestinationTermChange,
  migrateTermData,
}) => {
  const { terms, sourceTerm } = data;

  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA);

  const termOptions = _.reduce(
    terms,
    (acc, term) => {
      if (term.id == sourceTerm.id || term.type == "CUMULATIVE") return acc;

      acc.push({ label: term.label, value: term.id });
      return acc;
    },
    []
  );

  const handleTabChange = selectedTab => {
    setTab(selectedTab);
  };

  const handleMigrateData = async () => {
    setIsLoading(true);
    await migrateTermData({
      input: {
        issueType: "TRANSFER_ASSOCIATED_DATA_BETWEEN_ACADEMIC_TERMS",
        payload: {
          shouldUpdateAssessmentCategoriesData: true,
          sourceAcademicTermId: sourceTerm.id,
          targetAcademicTermId: destinationTerm.value,
        },
      },
    });

    setTab(TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA);
    handleDestinationTermChange(null);
    setIsLoading(false);
  };

  const getDownloadFileName = () => {
    const isAssociatedDataTab =
      tab === TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA;

    if (isAssociatedDataTab) {
      return `${sourceTerm.label}_associated_data`;
    } else {
      return `${destinationTerm.label}_missing_categories`;
    }
  };

  const getHeaderTitle = () => {
    const isAssociatedDataTab =
      tab === TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA;

    if (isAssociatedDataTab) {
      return "View source term associated data";
    } else {
      return "Categories needed in target term";
    }
  };

  const isAssociatedDataTab =
    tab === TAB_STATE.VIEW_SOURCE_TERM_ASSOCIATED_DATA;

  const currentTabFeedData = isAssociatedDataTab
    ? getAssociatedDataFeedData(associatedDataOfAcademicTerm)
    : getMissingCategoriesFeedData(missingCategoriesForAcademicTermMigration);

  const currentTabColumnList = isAssociatedDataTab
    ? getAssociatedDataColumnList()
    : getMissingCategoriesColumnList();

  const shouldShowDownloadButton = () => {
    return !_.isEmpty(currentTabFeedData);
  };

  const renderTableContent = () => {
    const sourceTerm = data.sourceTerm;
    const destinationTermLabel = destinationTerm.label;

    const emptyStateTitle = isAssociatedDataTab
      ? "No associated data found"
      : "All categories exist";

    const emptyStateSubtitle = isAssociatedDataTab
      ? `No associated data found for academic term "${sourceTerm.label}"`
      : `All required categories exist in target academic term "${destinationTermLabel}"`;

    const emptyStateIllustration = isAssociatedDataTab
      ? NoDataIllustration
      : AllCompletedIllustration;

    if (_.isEmpty(currentTabFeedData)) {
      return (
        <div className={classes.emptyStateContainer}>
          <EmptyState
            title={emptyStateTitle}
            subtitle={emptyStateSubtitle}
            subtitleStyle={styles.emptyStateSubtitleStyle}
            illustration={emptyStateIllustration}
          />
        </div>
      );
    }

    return (
      <div className={classes.tableContainer}>
        <FeedListv2
          columnList={currentTabColumnList}
          feedData={currentTabFeedData}
          enableStickyHeader={true}
          headerTheme="subtle"
          showBorder={true}
          getCustomComponent={
            isAssociatedDataTab ? getCustomComponent : undefined
          }
        />
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.leftContainer}>
        <h1 className="heading-1">Migrate term data</h1>
        <div className={classes.dropdownContainer}>
          <div>Source term:</div>
          <SelectDropdown
            onChange={() => {}}
            value={sourceTerm}
            options={termOptions}
            isClearable={false}
            placeholder={"Select source term"}
            isDisabled={true}
          />
        </div>
        <div className={classes.dropdownContainer}>
          <div>Destination term:</div>
          <SelectDropdown
            onChange={handleDestinationTermChange}
            value={destinationTerm}
            options={termOptions}
            isClearable={false}
            placeholder={"Select destination term"}
          />
        </div>
        <div className={classes.buttonContainer}>
          <Button
            variant="progressive"
            onClick={handleMigrateData}
            disabled={!destinationTerm || !areNoMissingCategoriesInTargetTerm}
          >
            Migrate Data
          </Button>
        </div>
        <div className={classes.note}>
          Note: The &quot;Migrate Data&quot; button will remain disabled until a
          destination term is selected.
        </div>
        <div className={classes.note}>
          Note: Go to the second tab to view the categories required in the
          destination term. If any categories need to be created, please create
          them first and then repeat the process. Once all categories are
          available, the button will be enabled.
        </div>
      </div>
      {destinationTerm ? (
        <div className={classes.rightContainer}>
          <div className={classes.header}>
            <h2 className="heading-2">{getHeaderTitle()}</h2>
            {shouldShowDownloadButton() && (
              <DownloadTableData
                feedData={currentTabFeedData}
                columnList={currentTabColumnList}
                fileName={getDownloadFileName()}
              />
            )}
          </div>
          <div className={classes.tabsContainer}>
            <Tabs
              options={TABS}
              value={tab}
              type="fixed"
              variant="container"
              containerStyle={styles.tabStyle}
              onChange={handleTabChange}
            />
          </div>
          {renderTableContent()}
        </div>
      ) : null}
      {isLoading ? <Loading /> : null}
    </div>
  );
};

const mapActionCreators = { migrateTermData };

const ViewAndMigrateTermAssociatedDataHOC = compose(
  connect(null, mapActionCreators),
  graphql(getMissingCategoriesForMigrationQuery, {
    name: "getMissingCategoriesForMigrationQuery",
    skip: ({ skipMissingCategoriesAndAssociatedDataQuery }) =>
      skipMissingCategoriesAndAssociatedDataQuery,
    options: ({ missingCategoriesForMigrationQueryInput }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: missingCategoriesForMigrationQueryInput,
      };
    },
    props: ({
      getMissingCategoriesForMigrationQuery,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(
        getMissingCategoriesForMigrationQuery,
        "documentation",
        {}
      );

      const missingCategoriesForAcademicTermMigration = _.get(
        queryData,
        "internalToolsData.progressReport.missingCategoriesForAcademicTermMigration",
        []
      );

      const areNoMissingCategoriesInTargetTerm = _.isEmpty(
        missingCategoriesForAcademicTermMigration
      );

      return {
        missingCategoriesForAcademicTermMigration,
        areNoMissingCategoriesInTargetTerm,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          getMissingCategoriesForMigrationQuery["networkStatus"] == 1 ||
          getMissingCategoriesForMigrationQuery["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getAssociatedDataOfAcademicTermQuery, {
    name: "getAssociatedDataOfAcademicTermQuery",
    skip: ({ skipMissingCategoriesAndAssociatedDataQuery }) =>
      skipMissingCategoriesAndAssociatedDataQuery,
    options: ({ associatedDataWithAcademicTermInput }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: associatedDataWithAcademicTermInput,
      };
    },
    props: ({
      getAssociatedDataOfAcademicTermQuery,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(
        getAssociatedDataOfAcademicTermQuery,
        "documentation",
        {}
      );

      const associatedDataOfAcademicTerm = _.get(
        queryData,
        "internalToolsData.progressReport.associatedDataOfAcademicTerm",
        []
      );

      return {
        associatedDataOfAcademicTerm,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          getAssociatedDataOfAcademicTermQuery["networkStatus"] == 1 ||
          getAssociatedDataOfAcademicTermQuery["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(ViewAndMigrateTermAssociatedData);

const ViewAndMigrateTermAssociatedDataWrapper = ({ data }) => {
  const [destinationTerm, setDestinationTerm] = useState(null);

  const handleDestinationTermChange = selectedOption => {
    setDestinationTerm(selectedOption);
  };

  const missingCategoriesForMigrationQueryInput = {
    input: {
      sourceAcademicTermId: data.sourceTerm.id,
      targetAcademicTermId: destinationTerm?.value,
    },
  };

  const associatedDataWithAcademicTermInput = {
    input: {
      academicTermId: data.sourceTerm.id,
    },
  };

  const skipMissingCategoriesAndAssociatedDataQuery = _.isEmpty(
    destinationTerm
  );

  return (
    <ViewAndMigrateTermAssociatedDataHOC
      data={data}
      destinationTerm={destinationTerm}
      handleDestinationTermChange={handleDestinationTermChange}
      missingCategoriesForMigrationQueryInput={
        missingCategoriesForMigrationQueryInput
      }
      associatedDataWithAcademicTermInput={associatedDataWithAcademicTermInput}
      skipMissingCategoriesAndAssociatedDataQuery={
        skipMissingCategoriesAndAssociatedDataQuery
      }
      isData={true}
      isLoading={false}
    />
  );
};

export default ViewAndMigrateTermAssociatedDataWrapper;
